import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api'
import { usePowerTariff, useUpdateTariff, type PowerTariff } from '@/lib/power'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

type Draft = Omit<PowerTariff, 'tempo_rates'>

export function PowerSettingsPage() {
  const { data, isLoading } = usePowerTariff()
  const update = useUpdateTariff()
  const [draft, setDraft] = useState<Draft | null>(null)

  useEffect(() => {
    if (data) {
      const { tempo_rates: _ignored, ...rest } = data
      setDraft({ ...rest, hc_windows: rest.hc_windows ?? [] })
    }
  }, [data])

  if (isLoading || !draft) return <Skeleton className="h-96" />

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft({ ...draft, [k]: v })

  const setWindow = (i: number, field: 'start' | 'end', v: string) => {
    const w = [...draft.hc_windows]
    w[i] = { ...w[i], [field]: v }
    set('hc_windows', w)
  }
  const addWindow = () => set('hc_windows', [...draft.hc_windows, { start: '22:00', end: '06:00' }])
  const removeWindow = (i: number) =>
    set('hc_windows', draft.hc_windows.filter((_, j) => j !== i))

  async function onSave() {
    try {
      await update.mutateAsync(draft!)
      toast.success('Tariff saved')
    } catch (e) {
      toast.error(
        e instanceof ApiError
          ? ((e.detail as { detail?: string })?.detail ?? e.message)
          : 'Save failed',
      )
    }
  }

  const num = (v: number | null) => (v == null ? '' : String(v))
  const parseNum = (s: string): number | null => (s.trim() === '' ? null : Number(s))

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Power tariff</h1>
        <p className="text-muted-foreground">
          Used to price energy accurately, by time-of-use band. Cost is computed by integrating
          the measured power series against these bands.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contract</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Option</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                value={draft.option}
                onChange={(e) => set('option', e.target.value)}
              >
                <option value="base">Base (single rate)</option>
                <option value="hphc">Heures Pleines / Creuses</option>
                {/* Tempo is not offered: the cost engine only prices base and
                    hp/hc, so a Tempo contract would silently bill at rate_hp /
                    rate_hc — normally NULL under Tempo, i.e. €0.00 everywhere.
                    The API rejects it too; re-add once _rate_for handles it. */}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Input value={draft.currency} onChange={(e) => set('currency', e.target.value)} />
            </div>
          </div>

          {draft.option === 'base' && (
            <div className="space-y-2 max-w-xs">
              <Label>Rate (€/kWh)</Label>
              <Input
                type="number"
                step="0.0001"
                value={num(draft.rate_base)}
                onChange={(e) => set('rate_base', parseNum(e.target.value))}
              />
            </div>
          )}

          {draft.option === 'hphc' && (
            <>
              <div className="grid grid-cols-2 gap-4 max-w-md">
                <div className="space-y-2">
                  <Label>Heures Pleines (€/kWh)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={num(draft.rate_hp)}
                    onChange={(e) => set('rate_hp', parseNum(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heures Creuses (€/kWh)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={num(draft.rate_hc)}
                    onChange={(e) => set('rate_hc', parseNum(e.target.value))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Heures Creuses windows (local time)</Label>
                <p className="text-xs text-muted-foreground">
                  Use the exact slots from your bill (Enedis-assigned). Windows may wrap midnight
                  (e.g. 22:00 → 06:00).
                </p>
                {draft.hc_windows.map((w, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      type="time"
                      className="w-32"
                      value={w.start}
                      onChange={(e) => setWindow(i, 'start', e.target.value)}
                    />
                    <span className="text-muted-foreground">→</span>
                    <Input
                      type="time"
                      className="w-32"
                      value={w.end}
                      onChange={(e) => setWindow(i, 'end', e.target.value)}
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeWindow(i)}>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addWindow}>
                  Add window
                </Button>
              </div>
            </>
          )}

          {draft.option === 'tempo' && (
            <p className="text-sm text-destructive">
              Tempo is not supported yet — energy cost would be computed at the
              HP/HC rates and read €0.00. Switch to Base or HP/HC.
            </p>
          )}

          {/* No subscription field: reported costs are energy only, so a flat
              monthly fee has no effect on any figure Zeus displays. */}
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Input value={draft.tz} onChange={(e) => set('tz', e.target.value)} />
            </div>
          </div>

          <Button onClick={() => void onSave()} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save tariff'}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
